import React from 'react';
import { __DEV__ } from './helpers';

export interface UseControllableStateProps<T> {
  /**
   * The value of the state. If none is provided, this hook will create an uncontrolled state for you. If one is provided,
   * then you're expected to also provide an `onChange
   */
  value?: T;
  /**
   * An optional default value for the state
   */
  defaultValue?: T;
  /**
   * The handler for when the value changes. If you didn't provide a `value` prop, then you don't need to set this. If
   * you did though, then this function will be called as a state-setter whenever the state should be updated
   */
  onChange?: (value: T) => void;
}

/**
 * This hook acts as a polymorphic `useState`. It can act either as controlled or uncontrolled state hook, depending on
 * whether the props `value` and `onChange` are defined or not
 */
function useControllableState<T = any>({
  value: controlledValue,
  defaultValue,
  onChange: controlledOnChange,
}: UseControllableStateProps<T>) {
  if (__DEV__) {
    if (
      (controlledOnChange !== undefined && controlledValue === undefined) ||
      (controlledOnChange === undefined && controlledValue !== undefined)
    ) {
      // eslint-disable-next-line no-console
      console.error(
        'You need to define both `value` and `onChange` for the component to be controlled correctly',
      );
    }
  }

  const [uncontrolledState, setUncontrolledState] = React.useState<T>(defaultValue);
  const isControlled = controlledOnChange !== undefined;
  const value = isControlled ? (controlledValue ?? defaultValue) : uncontrolledState;

  const setValue = React.useCallback(
    (next: React.SetStateAction<T>) => {
      const setter = next as (prevState?: T) => T;
      const nextValue = typeof next === 'function' ? setter(value) : next;

      if (!isControlled) {
        setUncontrolledState(nextValue);
      } else {
        controlledOnChange(nextValue);
      }
    },
    [isControlled, controlledOnChange, value],
  );

  return [value, setValue] as [T, React.Dispatch<React.SetStateAction<T>>];
}

export default useControllableState;
